package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.PlatformOverviewDto
import com.qrkontaq.data.dto.PlatformPaymentDto
import com.qrkontaq.data.dto.PlatformRestaurantDto
import com.qrkontaq.data.repository.PlatformRepository
import com.qrkontaq.data.repository.PlatformResult
import com.qrkontaq.ui.tenant.InlineIcon
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

private val EMERALD = "#34D399"
private val AMBER = "#E8A33D"
private val GREY = "#8699BE"

// ---- Overview ----

@Composable
internal fun PlatformOverviewView(overview: PlatformOverviewDto) {
    val currency = overview.currency

    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 12.px)) {
        QpKpi(AdminIcons.Wallet, PlatformT.kpiMrr, money(overview.mrrMinor, currency), EMERALD)
        QpKpi(AdminIcons.ChartBar, PlatformT.kpiRevenue30d, money(overview.revenue30dMinor, currency), EMERALD)
        QpKpi(AdminIcons.CreditCard, PlatformT.kpiRevenueTotal, money(overview.revenueTotalMinor, currency), AMBER)
        QpKpi(AdminIcons.Gift, PlatformT.kpiGmv30d, money(overview.guestGmv30dMinor, currency), GREY)
    }
    Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(12.px).margin(bottom = 14.px)) {
        QpKpi(AdminIcons.Dashboard, PlatformT.kpiVenues, "${overview.restaurantsTotal}", EMERALD)
        QpKpi(AdminIcons.Users, PlatformT.kpiUsers, "${overview.usersTotal}", AMBER)
        QpKpi(AdminIcons.ChefHat, PlatformT.kpiOrders30d, "${overview.orders30d}", EMERALD)
        QpKpi(AdminIcons.QrCode, PlatformT.kpiScans30d, "${overview.scans30d}", GREY)
    }

    QpCard {
        QpTitle(PlatformT.subsTitle)
        Row(Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(8.px)) {
            SubsChip(PlatformT.subsFree, overview.subsFree, GREY)
            SubsChip(PlatformT.subsActive, overview.subsActive, EMERALD)
            SubsChip(PlatformT.subsPastDue, overview.subsPastDue, "#FF6B5B")
            SubsChip(PlatformT.subsAutoRenew, overview.autoRenewOn, "#7EE2BC")
        }
    }

    QpCard {
        QpTitle(PlatformT.chartRevenue)
        QpAreaChart(
            labels = overview.revenueByMonth.map { it.month.takeLast(2) },
            series = listOf(
                AreaSeries(PlatformT.kpiRevenue30d, EMERALD, overview.revenueByMonth.map { it.amountMinor / 100.0 }),
            ),
        )
    }
    QpTwoColumn(
        first = {
            QpCard {
                QpTitle(PlatformT.chartNewVenues)
                QpAreaChart(
                    labels = overview.newRestaurantsByDay.map { it.date.takeLast(2) },
                    series = listOf(AreaSeries(PlatformT.navVenues, EMERALD, overview.newRestaurantsByDay.map { it.count.toDouble() })),
                )
            }
        },
        second = {
            QpCard {
                QpTitle(PlatformT.chartNewUsers)
                QpAreaChart(
                    labels = overview.newUsersByDay.map { it.date.takeLast(2) },
                    series = listOf(AreaSeries(PlatformT.kpiUsers, AMBER, overview.newUsersByDay.map { it.count.toDouble() })),
                )
            }
        },
    )
}

@Composable
private fun SubsChip(
    label: String,
    count: Int,
    color: String,
) {
    Row(
        Modifier
            .padding(topBottom = 7.px, leftRight = 13.px)
            .borderRadius(999.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
            .gap(7.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(Modifier.size(8.px).borderRadius(999.px).styleModifier { property("background", color) })
        SpanText(label, Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.6f)))
        SpanText("$count", Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)))
    }
}

// ---- Venues ----

/** A pending destructive action, kept while the confirm dialog is open. */
private sealed interface PendingDelete {
    val venue: PlatformRestaurantDto

    data class Venue(override val venue: PlatformRestaurantDto) : PendingDelete

    data class Owner(override val venue: PlatformRestaurantDto) : PendingDelete
}

@Composable
internal fun PlatformVenuesView(
    venues: List<PlatformRestaurantDto>,
    repository: PlatformRepository,
    onDeleted: () -> Unit,
) {
    var pending by remember { mutableStateOf<PendingDelete?>(null) }

    QpCard {
        QpTitle("${PlatformT.venuesTitle} · ${venues.size}")
        if (venues.isEmpty()) {
            SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
        } else {
            Box(Modifier.fillMaxWidth().styleModifier { property("overflow-x", "auto") }) {
                Column(Modifier.fillMaxWidth().styleModifier { property("min-width", "1060px") }) {
                    QpTableHeader(
                        PlatformT.colVenue to 2.2,
                        PlatformT.colOwner to 1.9,
                        PlatformT.colPlan to 0.9,
                        PlatformT.colStatus to 1.1,
                        PlatformT.colPaidUntil to 1.0,
                        PlatformT.colOrders30d to 0.9,
                        PlatformT.colRevenue30d to 1.0,
                        PlatformT.colCreated to 1.0,
                        "" to 0.8,
                    )
                    venues.forEach { VenueRow(it) { action -> pending = action } }
                }
            }
        }
    }

    pending?.let { target ->
        DeleteConfirmDialog(
            target = target,
            repository = repository,
            onClose = { pending = null },
            onDeleted = onDeleted,
        )
    }
}

@Composable
private fun VenueRow(
    venue: PlatformRestaurantDto,
    onDelete: (PendingDelete) -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().padding(topBottom = 10.px).gap(10.px).attrsModifier { classes("qp-trow") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        QpCell(2.2) {
            Column {
                SpanText(
                    venue.name,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4))
                        .attrsModifier { classes("qk-clamp1") },
                )
                SpanText("${venue.slug}.qrkontaq.com", Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.45f)))
            }
        }
        QpCell(1.9) {
            Column {
                SpanText(
                    venue.ownerName ?: "—",
                    Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)).attrsModifier { classes("qk-clamp1") },
                )
                SpanText(
                    venue.ownerEmail ?: "",
                    Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.45f)).attrsModifier { classes("qk-clamp1") },
                )
            }
        }
        QpCell(0.9) { SpanText(platformPlanLabel(venue.plan), Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f))) }
        QpCell(1.1) { SubscriptionStatusChip(venue.subscriptionStatus, venue.autoRenew) }
        QpCell(1.0) {
            SpanText(
                isoDay(venue.paidUntil),
                Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)),
            )
        }
        QpCell(0.9) { SpanText("${venue.orders30d}", Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f))) }
        QpCell(1.0) {
            SpanText(money(venue.revenue30dMinor, venue.currency), Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)))
        }
        QpCell(1.0) { SpanText(isoDay(venue.createdAt), Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.45f))) }
        QpCell(0.8) {
            Row(Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                RowActionButton(AdminIcons.Trash, PlatformT.actDeleteVenue) { onDelete(PendingDelete.Venue(venue)) }
                if (venue.ownerId != null) {
                    RowActionButton(AdminIcons.UserX, PlatformT.actDeleteOwner) { onDelete(PendingDelete.Owner(venue)) }
                }
            }
        }
    }
}

/** Small red-tinted icon button used for the per-row destructive actions. */
@Composable
private fun RowActionButton(
    icon: String,
    label: String,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .size(30.px)
            .minWidth(30.px)
            .borderRadius(9.px)
            .backgroundColor(Color.rgba(255, 107, 91, 0.10f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 107, 91, 0.25f))
            .cursor(Cursor.Pointer)
            .attrsModifier {
                classes("qp-chip")
                attr("role", "button")
                attr("aria-label", label)
                attr("title", label)
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(icon, 14, "#FF6B5B")
    }
}

/**
 * Full-screen confirm popup for the destructive platform actions. Runs the delete itself,
 * closes on success and asks the screen to refetch; failures keep it open with an error line.
 */
@Composable
private fun DeleteConfirmDialog(
    target: PendingDelete,
    repository: PlatformRepository,
    onClose: () -> Unit,
    onDeleted: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var failed by remember { mutableStateOf(false) }

    val title: String
    val text: String
    when (target) {
        is PendingDelete.Venue -> {
            title = PlatformT.delVenueTitle
            text = PlatformT.delVenueText.replace("{name}", target.venue.name)
        }
        is PendingDelete.Owner -> {
            title = PlatformT.delOwnerTitle
            text = PlatformT.delOwnerText.replace("{email}", target.venue.ownerEmail ?: target.venue.ownerId.orEmpty())
        }
    }

    Box(
        Modifier
            .position(Position.Fixed)
            .styleModifier {
                property("inset", "0")
                property("z-index", "400")
                property("background", "rgba(4, 12, 9, 0.66)")
                property("backdrop-filter", "blur(6px)")
                property("-webkit-backdrop-filter", "blur(6px)")
            }
            .onClick { if (!busy) onClose() },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            Modifier
                .maxWidth(440.px)
                .fillMaxWidth(92.percent)
                .margin(leftRight = 16.px)
                .attrsModifier { classes("qp-card") }
                .onClick { it.stopPropagation() },
        ) {
            SpanText(
                title,
                Modifier.fontSize(17.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)).margin(bottom = 8.px),
            )
            SpanText(
                text,
                Modifier.fontSize(13.5.px).lineHeight(1.55).color(Color.rgba(242, 247, 244, 0.65f)).margin(bottom = 16.px),
            )
            if (failed) {
                SpanText(
                    PlatformT.delFailed,
                    Modifier.fontSize(12.5.px).color(Color.rgb(0xFF, 0x6B, 0x5B)).margin(bottom = 12.px),
                )
            }
            Row(
                Modifier.fillMaxWidth().gap(10.px),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    Modifier
                        .padding(topBottom = 9.px, leftRight = 16.px)
                        .borderRadius(11.px)
                        .backgroundColor(Color.rgba(255, 255, 255, 0.06f))
                        .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.12f))
                        .cursor(Cursor.Pointer)
                        .attrsModifier { classes("qp-chip"); attr("role", "button") }
                        .onClick { if (!busy) onClose() },
                ) {
                    SpanText(PlatformT.delCancel, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.85f)))
                }
                Box(
                    Modifier
                        .padding(topBottom = 9.px, leftRight = 16.px)
                        .borderRadius(11.px)
                        .backgroundColor(if (busy) Color.rgba(255, 107, 91, 0.4f) else Color.rgb(0xE5, 0x48, 0x3B))
                        .cursor(if (busy) Cursor.Default else Cursor.Pointer)
                        .attrsModifier { classes("qp-chip"); attr("role", "button") }
                        .onClick {
                            if (busy) return@onClick
                            busy = true
                            failed = false
                            scope.launch {
                                val result =
                                    when (target) {
                                        is PendingDelete.Venue -> repository.deleteRestaurant(target.venue.id)
                                        is PendingDelete.Owner -> repository.deleteUser(target.venue.ownerId!!)
                                    }
                                if (result is PlatformResult.Ok) {
                                    onClose()
                                    onDeleted()
                                } else {
                                    failed = true
                                    busy = false
                                }
                            }
                        },
                ) {
                    SpanText(
                        if (busy) PlatformT.delWorking else PlatformT.delConfirm,
                        Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Color.rgb(255, 255, 255)),
                    )
                }
            }
        }
    }
}

// ---- Payments ----

@Composable
internal fun PlatformPaymentsView(payments: List<PlatformPaymentDto>) {
    QpCard {
        QpTitle("${PlatformT.paymentsTitle} · ${payments.size}")
        if (payments.isEmpty()) {
            SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
        } else {
            Box(Modifier.fillMaxWidth().styleModifier { property("overflow-x", "auto") }) {
                Column(Modifier.fillMaxWidth().styleModifier { property("min-width", "760px") }) {
                    QpTableHeader(
                        PlatformT.colDate to 1.1,
                        PlatformT.colVenue to 2.0,
                        PlatformT.colPlan to 0.9,
                        PlatformT.colAmount to 1.0,
                        PlatformT.colStatus to 1.0,
                        PlatformT.colType to 1.0,
                    )
                    payments.forEach { PaymentRow(it) }
                }
            }
        }
    }
}

@Composable
private fun PaymentRow(payment: PlatformPaymentDto) {
    Row(
        Modifier.fillMaxWidth().padding(topBottom = 10.px).gap(10.px).attrsModifier { classes("qp-trow") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        QpCell(1.1) {
            SpanText(
                isoDay(payment.paidAt ?: payment.createdAt),
                Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)),
            )
        }
        QpCell(2.0) {
            SpanText(
                payment.restaurantName,
                Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)).attrsModifier { classes("qk-clamp1") },
            )
        }
        QpCell(0.9) { SpanText(platformPlanLabel(payment.plan), Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f))) }
        QpCell(1.0) {
            SpanText(
                money(payment.amountMinor, payment.currency),
                Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)),
            )
        }
        QpCell(1.0) { PaymentStatusChip(payment.status) }
        QpCell(1.0) {
            SpanText(
                if (payment.recurring) PlatformT.payRecurring else PlatformT.payManual,
                Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.5f)),
            )
        }
    }
}

// ---- shared table + status chips ----

@Composable
private fun QpTableHeader(vararg columns: Pair<String, Double>) {
    Row(
        Modifier.fillMaxWidth().padding(topBottom = 8.px).gap(10.px)
            .borderBottom(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f)),
    ) {
        columns.forEach { (t, w) ->
            SpanText(t, Modifier.attrsModifier { classes("qp-th") }.styleModifier { property("flex", "$w"); property("min-width", "0") })
        }
    }
}

@Composable
private fun QpCell(
    weight: Double,
    content: @Composable () -> Unit,
) {
    Box(Modifier.styleModifier { property("flex", "$weight"); property("min-width", "0") }) { content() }
}

@Composable
private fun SubscriptionStatusChip(
    status: String?,
    autoRenew: Boolean,
) {
    val (label, color) =
        when (status) {
            "FREE" -> PlatformT.subsFree to GREY
            "ACTIVE" -> PlatformT.subsActive to EMERALD
            "PAST_DUE" -> PlatformT.subsPastDue to "#FF6B5B"
            else -> "—" to GREY
        }
    Row(Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(8.px).minWidth(8.px).borderRadius(999.px).styleModifier { property("background", color) })
        SpanText(
            label + if (autoRenew) " ⟳" else "",
            Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.85f)),
        )
    }
}

@Composable
private fun PaymentStatusChip(status: String) {
    val color =
        when (status) {
            "SUCCESS" -> EMERALD
            "FAILED", "EXPIRED" -> "#FF6B5B"
            else -> AMBER
        }
    Row(Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(8.px).minWidth(8.px).borderRadius(999.px).styleModifier { property("background", color) })
        SpanText(status, Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.5f)))
    }
}
